
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { MealComposition, MealCompositionWithRecipes } from '@/types/mealComposition';
import { Recipe } from '@/types/recipe';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import MealCompositionFormFields from './MealCompositionFormFields';
import SelectedRecipesList from './SelectedRecipesList';
import RecipeSelectionModal from '../composition/RecipeSelectionModal';

interface MealCompositionFormData {
  name: string;
  recipeIds: string[];
}

interface MealCompositionFormProps {
  composition?: MealCompositionWithRecipes;
  recipes: Recipe[];
  onSubmit: (data: Omit<MealComposition, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  onSuccess?: () => void;
}

const MealCompositionForm: React.FC<MealCompositionFormProps> = ({
  composition,
  recipes,
  onSubmit,
  onCancel,
  isLoading = false,
  onSuccess
}) => {
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<string[]>(
    composition?.recipeIds || []
  );
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<MealCompositionFormData>({
    defaultValues: {
      name: composition?.name || '',
      recipeIds: composition?.recipeIds || []
    }
  });

  const watchedName = watch('name');

  useEffect(() => {
    setValue('recipeIds', selectedRecipeIds);
  }, [selectedRecipeIds, setValue]);

  const selectedRecipes = recipes.filter(recipe =>
    selectedRecipeIds.includes(recipe.id)
  );

  const handleRecipeSelection = (newSelectedIds: string[]) => {
    setSelectedRecipeIds(newSelectedIds);
  };

  const handleRemoveRecipe = (recipeId: string) => {
    setSelectedRecipeIds(prev => prev.filter(id => id !== recipeId));
  };

  const onFormSubmit = async (data: MealCompositionFormData) => {
    if (selectedRecipeIds.length === 0) {
      toast.error('Please add at least one recipe to the composition');
      return;
    }

    try {
      await onSubmit({
        name: data.name,
        recipeIds: selectedRecipeIds,
        familyId: composition?.familyId || null,
        tags: composition?.tags || []
      });
      
      // Reset form for new compositions only (not edits)
      if (!composition) {
        reset({ name: '', recipeIds: [] });
        setSelectedRecipeIds([]);
      }
      
      // Call onSuccess for editing existing compositions
      if (composition && onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error('Failed to save meal composition');
    }
  };

  return (
    <>
      <div className="w-full space-y-6">
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <MealCompositionFormFields register={register} errors={errors} />

          <SelectedRecipesList 
            selectedRecipes={selectedRecipes}
            onRemoveRecipe={handleRemoveRecipe}
          />

          <Separator />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Recipes</h3>
              <span className="text-xs text-muted-foreground">
                {selectedRecipeIds.length} selected
              </span>
            </div>
            
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsRecipeModalOpen(true)}
              className="w-full justify-center gap-2"
              disabled={isLoading}
            >
              <Plus size={16} />
              {selectedRecipeIds.length === 0 ? 'Add Recipes' : 'Add More Recipes'}
            </Button>
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={isLoading || !watchedName.trim() || selectedRecipeIds.length === 0}
              className="flex-1"
            >
              {isLoading ? 'Saving...' : composition ? 'Update Composition' : 'Create Composition'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>

      <RecipeSelectionModal
        isOpen={isRecipeModalOpen}
        onClose={() => setIsRecipeModalOpen(false)}
        onSelect={handleRecipeSelection}
        recipes={recipes}
        selectedRecipeIds={selectedRecipeIds}
      />
    </>
  );
};

export default MealCompositionForm;
